import React, { useCallback, useEffect, useState } from 'react';
import config from '../../config';
import {
  buildBookingLeadBody,
  createBookingLead,
  BOOKING_LEAD_SOURCE,
  validateBookingLeadInputs,
} from '../../utils/bookingLeadsApi';
import s from './CreateBookingLeadModal.module.scss';

const API = config.customerApiBase;

const VEHICLE_CLASS_MAP = {
  1: 'Piaggio Ape',
  2: 'Tata Ace',
  3: 'Ashok Leyland Dost',
  4: 'Force Tempo',
  5: 'Tata 407',
  6: 'Eicher Pro 1049',
  7: 'Eicher Pro 1059',
  8: 'Eicher Pro 2080',
  9: 'Partner',
  10: 'Tata LPT 1109',
  11: 'Furio 18',
  12: 'Eicher Pro 6032',
  13: 'Tata LPT 1818',
  14: 'Ashok Leyland 2525',
  15: 'Tata Signa 3523',
};

const EQUIPMENT_CLASS_MAP = {
  51: 'Crane - 9 Ton',
  52: 'Crane - 12 Ton',
  56: 'Crane - 14 Ton',
  57: 'Crane - 15 Ton',
};

function normalizePhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 10) return digits;
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  return digits;
}

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function CreateBookingLeadModal({
  open,
  onClose,
  member,
  leadSource = BOOKING_LEAD_SOURCE.CALL_MANAGER,
  onCreated,
  showToast,
}) {
  const phone = normalizePhone(member?.phone_e164 || member?.phone);
  const displayName = member?.name || member?.greeting_name || member?.business_name || 'Contact';

  const [form, setForm] = useState({
    lead_type: 'future_booking',
    contact_by: new Date().toISOString().slice(0, 16),
    pickup_time: '',
    vehicle_class_id: '',
    remarks: '',
  });
  const [pickupAddress, setPickupAddress] = useState(null);
  const [dropAddresses, setDropAddresses] = useState([]);
  const [recommendedAddresses, setRecommendedAddresses] = useState([]);
  const [addressQuery, setAddressQuery] = useState('');
  const [dropQuery, setDropQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [dropSuggestions, setDropSuggestions] = useState([]);
  const [addressSearching, setAddressSearching] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      lead_type: 'future_booking',
      contact_by: new Date().toISOString().slice(0, 16),
      pickup_time: '',
      vehicle_class_id: '',
      remarks: '',
    });
    setPickupAddress(null);
    setDropAddresses([]);
    setAddressQuery('');
    setDropQuery('');
    setAddressSuggestions([]);
    setDropSuggestions([]);
  }, [open, phone]);

  useEffect(() => {
    if (!open || !member?.customer_id) {
      setRecommendedAddresses([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/customers/${member.customer_id}/address-recommendations`, {
          headers: authHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setRecommendedAddresses(data.addresses || []);
        }
      } catch { /* ignore */ }
    })();
  }, [open, member?.customer_id]);

  const addressDisplay = (addr) => {
    if (!addr) return '';
    if (addr.text) return addr.text;
    const parts = [addr.locality, addr.city, addr.state].filter(Boolean);
    return parts.join(', ') || `Address #${addr.id}`;
  };

  const searchAddress = useCallback(async (query, kind) => {
    if (query.trim().length < 3) return;
    setAddressSearching(kind);
    try {
      const res = await fetch(`${API}/resolve-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ address_text: query.trim() }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const suggestions = data.results || [];
      if (kind === 'pickup') setAddressSuggestions(suggestions);
      else setDropSuggestions(suggestions);
    } catch { /* ignore */ } finally {
      setAddressSearching(null);
    }
  }, []);

  const applySuggestion = (suggestion, kind) => {
    const text = addressDisplay(suggestion);
    const entry = { id: suggestion.id, text };
    if (kind === 'pickup') {
      setPickupAddress(entry);
      setAddressSuggestions([]);
      setAddressQuery('');
    } else {
      setDropAddresses((prev) => [...prev, entry]);
      setDropSuggestions([]);
      setDropQuery('');
    }
  };

  const handleSubmit = async () => {
    const validationError = validateBookingLeadInputs({
      phone,
      leadType: form.lead_type,
      contactBy: form.contact_by,
      hasSelectedCustomer: !!member,
    });
    if (validationError) {
      showToast?.(validationError, 'error');
      return;
    }
    setSaving(true);
    try {
      const body = buildBookingLeadBody({
        phone,
        leadType: form.lead_type,
        contactBy: form.contact_by,
        source: leadSource,
        pickupTime: form.pickup_time,
        pickupAddressId: pickupAddress?.id,
        dropAddressIds: dropAddresses.map((a) => a.id).filter(Boolean),
        vehicleClassId: form.vehicle_class_id || undefined,
        remarks: form.remarks,
        member,
      });
      const data = await createBookingLead(body, { apiBase: API });

      const leadId = data.lead_id;
      if (!leadId) throw new Error('Lead created but no lead_id returned');
      if (data.duplicate_warning) {
        showToast?.(
          `Lead #${leadId} created (active lead #${data.duplicate_warning.existing_lead_id} exists)`,
          'success',
        );
      } else {
        showToast?.(`Lead #${leadId} created`, 'success');
      }
      onCreated?.(leadId);
      onClose?.();
    } catch (err) {
      showToast?.(err.message || 'Failed to create lead', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className={s.backdrop} onClick={onClose}>
      <div className={s.modal} role="dialog" onClick={(e) => e.stopPropagation()}>
        <div className={s.header}>
          <h3>Create booking lead</h3>
          <p className={s.sub}>{displayName} · {phone}</p>
        </div>
        <div className={s.body}>
          <label className={s.field}>
            <span>Lead type *</span>
            <select value={form.lead_type} onChange={(e) => setForm((f) => ({ ...f, lead_type: e.target.value }))}>
              <option value="future_booking">Future Booking</option>
              <option value="part_load_interest">Part Load Interest</option>
              <option value="general_inquiry">General Inquiry</option>
              <option value="equipment_interest">Equipment Interest</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className={s.field}>
            <span>Contact by *</span>
            <input type="datetime-local" value={form.contact_by} onChange={(e) => setForm((f) => ({ ...f, contact_by: e.target.value }))} />
          </label>
          <label className={s.field}>
            <span>Pickup time</span>
            <input type="datetime-local" value={form.pickup_time} onChange={(e) => setForm((f) => ({ ...f, pickup_time: e.target.value }))} />
          </label>
          <div className={s.field}>
            <span>Pickup address</span>
            {recommendedAddresses.length > 0 && !pickupAddress ? (
              <div className={s.chips}>
                {recommendedAddresses.map((addr) => (
                  <button key={addr.id} type="button" className={s.chip} onClick={() => setPickupAddress({ id: addr.id, text: addressDisplay(addr) })}>
                    {addressDisplay(addr)}
                  </button>
                ))}
              </div>
            ) : null}
            {!pickupAddress ? (
              <div className={s.addressRow}>
                <input
                  value={addressQuery}
                  placeholder="Search pickup address…"
                  onChange={(e) => setAddressQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchAddress(addressQuery, 'pickup'); } }}
                />
                <button type="button" onClick={() => searchAddress(addressQuery, 'pickup')} disabled={addressSearching === 'pickup'}>
                  {addressSearching === 'pickup' ? '…' : 'Search'}
                </button>
              </div>
            ) : (
              <div className={s.selectedChip}>
                <span>{pickupAddress.text}</span>
                <button type="button" onClick={() => setPickupAddress(null)}>×</button>
              </div>
            )}
            {addressSuggestions.length > 0 && !pickupAddress ? (
              <div className={s.suggestions}>
                {addressSuggestions.map((item, idx) => (
                  <button key={idx} type="button" onClick={() => applySuggestion(item, 'pickup')}>{addressDisplay(item)}</button>
                ))}
              </div>
            ) : null}
          </div>
          <div className={s.field}>
            <span>Drop addresses</span>
            {dropAddresses.length > 0 ? (
              <div className={s.dropList}>
                {dropAddresses.map((addr, idx) => (
                  <div key={idx} className={s.selectedChip}>
                    <span>{addr.text}</span>
                    <button type="button" onClick={() => setDropAddresses((prev) => prev.filter((_, i) => i !== idx))}>×</button>
                  </div>
                ))}
              </div>
            ) : null}
            <div className={s.addressRow}>
              <input
                value={dropQuery}
                placeholder="Search drop address…"
                onChange={(e) => setDropQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchAddress(dropQuery, 'drop'); } }}
              />
              <button type="button" onClick={() => searchAddress(dropQuery, 'drop')} disabled={addressSearching === 'drop'}>
                {addressSearching === 'drop' ? '…' : 'Search'}
              </button>
            </div>
            {dropSuggestions.length > 0 ? (
              <div className={s.suggestions}>
                {dropSuggestions.map((item, idx) => (
                  <button key={idx} type="button" onClick={() => applySuggestion(item, 'drop')}>{addressDisplay(item)}</button>
                ))}
              </div>
            ) : null}
          </div>
          <label className={s.field}>
            <span>Vehicle class</span>
            <select value={form.vehicle_class_id} onChange={(e) => setForm((f) => ({ ...f, vehicle_class_id: e.target.value }))}>
              <option value="">Select vehicle</option>
              <optgroup label="Trucks">
                {Object.entries(VEHICLE_CLASS_MAP).map(([id, name]) => (
                  <option key={id} value={id}>Class {id} — {name}</option>
                ))}
              </optgroup>
              <optgroup label="Equipment">
                {Object.entries(EQUIPMENT_CLASS_MAP).map(([id, name]) => (
                  <option key={id} value={id}>Class {id} — {name}</option>
                ))}
              </optgroup>
            </select>
          </label>
          <label className={s.field}>
            <span>Remarks</span>
            <textarea rows={2} value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} placeholder="Stored on the lead record (Leads page)" />
          </label>
        </div>
        <div className={s.footer}>
          <button type="button" className={s.btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" className={s.btnPrimary} onClick={handleSubmit} disabled={saving}>
            {saving ? 'Creating…' : 'Create lead'}
          </button>
        </div>
      </div>
    </div>
  );
}
